/** Sezione "Analisi AI" nel dettaglio di una voce watchlist (SPEC-007 §6). */

import { Link } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, EmptyState, Spinner } from '@/ui';
import { Markdown } from '@/ui/Markdown';
import {
  useAiAnalysisQuota,
  useInstrumentAnalyses,
  useRequestAnalysis,
  type AnalysisStatus,
  type AnalysisSummary,
} from './ai-analysis-api';
import { RATING_HINT, RATING_TONE } from './analysis-rating';
import type { WatchlistItem } from './api';

const SUPPORTED_ASSET_TYPES = new Set(['stock', 'etf', 'fund', 'crypto']);
const ACTIVE_STATUSES = new Set<AnalysisStatus>(['pending', 'running']);

function StatusBadge({ analysis }: { analysis: AnalysisSummary }) {
  if (analysis.status === 'completed' && analysis.rating) {
    return (
      <Badge tone={RATING_TONE[analysis.rating]} title={RATING_HINT[analysis.rating] ?? undefined}>
        {analysis.rating}
      </Badge>
    );
  }
  if (analysis.status === 'failed') return <Badge tone="negative">Fallita</Badge>;
  return <Badge tone="accent">In corso…</Badge>;
}

/**
 * Anteprima: solo intestazione + "ultimo capitolo" (la decisione finale del
 * Portfolio Manager, con la sua spiegazione) — il report completo, molto più
 * lungo, vive nella pagina di dettaglio dedicata.
 */
function FinalDecisionPreview({ analysis }: { analysis: AnalysisSummary }) {
  const targetLine = [
    analysis.priceTarget !== null ? `Obiettivo di prezzo: ${analysis.priceTarget}` : null,
    analysis.timeHorizon,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div className="ai-analysis__preview">
      {analysis.executiveSummary && <Markdown content={analysis.executiveSummary} />}
      {analysis.investmentThesis && <Markdown content={analysis.investmentThesis} />}
      {targetLine && <p className="ai-analysis__target">{targetLine}</p>}
    </div>
  );
}

export function AiAnalysisSection({ item }: { item: WatchlistItem }) {
  const instrumentId = item.instrument.id;
  const quotaQuery = useAiAnalysisQuota();
  const analysesQuery = useInstrumentAnalyses(instrumentId);
  const requestAnalysis = useRequestAnalysis(instrumentId);

  const assetTypeSupported = SUPPORTED_ASSET_TYPES.has(item.instrument.instrumentType);
  const quotaExhausted = quotaQuery.data ? quotaQuery.data.used >= quotaQuery.data.limit : false;
  const hasActiveAnalysis = analysesQuery.data?.items.some((analysis) => ACTIVE_STATUSES.has(analysis.status)) ?? false;
  const requestError = requestAnalysis.error instanceof ApiError ? requestAnalysis.error : null;

  return (
    <div className="ai-analysis">
      <div className="ai-analysis__toolbar">
        <Button
          onClick={() => requestAnalysis.mutate()}
          loading={requestAnalysis.isPending}
          disabled={!assetTypeSupported || quotaExhausted || hasActiveAnalysis}
        >
          Richiedi analisi AI
        </Button>
        {quotaQuery.data && (
          <span className="ai-analysis__quota">
            {quotaQuery.data.used}/{quotaQuery.data.limit} richieste oggi
          </span>
        )}
      </div>

      {!assetTypeSupported && (
        <Alert variant="info">
          Analisi AI non disponibile per strumenti di tipo &quot;{item.instrument.instrumentType}&quot;.
        </Alert>
      )}
      {assetTypeSupported && quotaExhausted && !hasActiveAnalysis && (
        <Alert variant="warning">Limite giornaliero di analisi AI raggiunto: riprova domani.</Alert>
      )}
      {requestError && (
        <Alert variant="error" title="Richiesta non riuscita">
          {requestError.message}
        </Alert>
      )}

      {analysesQuery.isPending ? (
        <Spinner label="Caricamento dello storico analisi…" />
      ) : analysesQuery.isError ? (
        <Alert variant="error" title="Impossibile caricare lo storico">
          {analysesQuery.error instanceof ApiError ? analysesQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      ) : analysesQuery.data.items.length === 0 ? (
        <EmptyState icon="🤖" title="Nessuna analisi AI ancora richiesta" />
      ) : (
        <ul className="ai-analysis__list">
          {analysesQuery.data.items.map((analysis) => (
            <li key={analysis.id} className="ai-analysis__item">
              <div className="ai-analysis__item-header">
                <StatusBadge analysis={analysis} />
                {analysis.triggeredBy === 'market_open_trigger' && (
                  <Badge tone="accent">🏛️ Trigger di mercato</Badge>
                )}
                <span className="ai-analysis__item-date">{formatDateTime(analysis.requestedAt)}</span>
                {(analysis.status === 'pending' || analysis.status === 'running') && analysis.progress && (
                  <span className="ai-analysis__progress">{analysis.progress}</span>
                )}
              </div>
              {analysis.status === 'failed' && analysis.errorMessage && (
                <Alert variant="error">{analysis.errorMessage}</Alert>
              )}
              {analysis.status === 'completed' && (
                <>
                  <FinalDecisionPreview analysis={analysis} />
                  <Link
                    className="ai-analysis__full-report-link"
                    to={`/watchlists/analyses/${instrumentId}/${analysis.id}`}
                  >
                    Report completo →
                  </Link>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
